import { useState, useEffect, Fragment } from 'react';
import {
  Package,
  Plus,
  Edit,
  Trash2,
  DollarSign,
  ChevronUp,
  Save,
  Loader2,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  X,
  PencilLine,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useRegionStore } from '@/stores/regionStore';
import { servicesAPI } from '@/api/services';
import { Button, Loading, ErrorMessage, Input } from '@/components/common';
import type {
  Service,
  ServiceType,
  CreateServiceData,
  ServicePricingDetails,
} from '@/types';

/**
 * ServiceManagementPage Component
 * Admin page for managing services and pricing (Manager role only)
 */
export const ServiceManagementPage = () => {
  const { services, loading, error, fetchServices } = useRegionStore();
  const [serviceTypeTab, setServiceTypeTab] = useState<'single_region' | 'route_based'>('single_region');
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [formData, setFormData] = useState<CreateServiceData>({
    nameEn: '',
    nameAr: '',
    descriptionEn: '',
    descriptionAr: '',
    serviceType: 'other',
    icon: '',
    displayOrder: 0,
    isActive: true,
  });
  const [submitting, setSubmitting] = useState(false);

  // Pricing management state
  const [expandedServiceId, setExpandedServiceId] = useState<number | null>(null);
  const [pricingDetails, setPricingDetails] = useState<ServicePricingDetails | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [editingPrices, setEditingPrices] = useState<Record<number, string>>({});
  const [savingRegions] = useState<Set<number>>(new Set());
  const [togglingRegions, setTogglingRegions] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState<'individual' | 'bulk'>('individual');

  // Edit Price Modal state
  const [editPriceModal, setEditPriceModal] = useState<{
    show: boolean;
    serviceId: number | null;
    regionId: number | null;
    regionName: string;
    currentPrice: number;
    newPrice: string;
  }>({
    show: false,
    serviceId: null,
    regionId: null,
    regionName: '',
    currentPrice: 0,
    newPrice: '',
  });
  const [savingModalPrice, setSavingModalPrice] = useState(false);
  const [bulkPrice, setBulkPrice] = useState('');
  const [selectedGovernorate, setSelectedGovernorate] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Service types
  const serviceTypes: Array<{ value: ServiceType; labelEn: string; labelAr: string }> = [
    { value: 'tire_change', labelEn: 'Tire Change', labelAr: 'تغيير الإطار' },
    {
      value: 'battery_subscription',
      labelEn: 'Battery Subscription',
      labelAr: 'اشتراك البطارية',
    },
    { value: 'fuel_delivery', labelEn: 'Fuel Delivery', labelAr: 'توصيل الوقود' },
    { value: 'external_patch', labelEn: 'External Patch', labelAr: 'الرقعه الخارجية' },
    { value: 'internal_patch', labelEn: 'Internal Patch', labelAr: 'الرقعه الداخليه' },
    {
      value: 'battery_transfer',
      labelEn: 'Battery Transfer',
      labelAr: 'نقل البطارية',
    },
    { value: 'other', labelEn: 'Other', labelAr: 'أخرى' },
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  const handleOpenModal = (service?: Service) => {
    if (service) {
      setEditingService(service);
      setFormData({
        nameEn: service.nameEn,
        nameAr: service.nameAr,
        descriptionEn: service.descriptionEn,
        descriptionAr: service.descriptionAr,
        serviceType: service.serviceType,
        icon: service.icon,
        displayOrder: service.displayOrder,
        isActive: service.isActive,
      });
    } else {
      setEditingService(null);
      setFormData({
        nameEn: '',
        nameAr: '',
        descriptionEn: '',
        descriptionAr: '',
        serviceType: 'other',
        icon: '',
        displayOrder: 0,
        isActive: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingService(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingService) {
        await servicesAPI.update(editingService.id, formData);
        toast.success('تم تحديث الخدمة بنجاح');
      } else {
        await servicesAPI.create(formData);
        toast.success('تم إضافة الخدمة بنجاح');
      }
      handleCloseModal();
      fetchServices();
    } catch (err: any) {
      toast.error(
        err.response?.data?.error?.message ||
          (editingService ? 'فشل تحديث الخدمة' : 'فشل إضافة الخدمة')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (service: Service) => {
    if (
      !window.confirm(
        `هل أنت متأكد من حذف الخدمة "${service.nameAr}"؟ سيتم حذف جميع الأسعار المرتبطة بها.`
      )
    ) {
      return;
    }

    try {
      await servicesAPI.delete(service.id);
      toast.success('تم حذف الخدمة بنجاح');
      fetchServices();
      if (expandedServiceId === service.id) {
        setExpandedServiceId(null);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل حذف الخدمة');
    }
  };

  const handleTogglePricing = async (serviceId: number) => {
    if (expandedServiceId === serviceId) {
      setExpandedServiceId(null);
      setPricingDetails(null);
      setEditingPrices({});
      setBulkPrice('');
      setSelectedGovernorate('');
      setActiveTab('individual');
    } else {
      setExpandedServiceId(serviceId);
      setLoadingPricing(true);
      try {
        const response = await servicesAPI.getPricingDetails(serviceId);
        setPricingDetails(response.data.data);
      } catch (err: any) {
        toast.error(err.response?.data?.error?.message || 'فشل تحميل بيانات الأسعار');
        setExpandedServiceId(null);
      } finally {
        setLoadingPricing(false);
      }
    }
  };

  const handlePriceChange = (regionId: number, value: string) => {
    setEditingPrices((prev) => ({
      ...prev,
      [regionId]: value,
    }));
  };

  const handleBulkUpdateAll = async () => {
    if (!expandedServiceId || !bulkPrice || parseFloat(bulkPrice) < 0) {
      toast.error('يرجى إدخال سعر صحيح');
      return;
    }

    if (
      !window.confirm(
        `هل أنت متأكد من تحديث أسعار جميع المناطق إلى ${bulkPrice} دينار؟`
      )
    ) {
      return;
    }

    setBulkUpdating(true);
    try {
      const response = await servicesAPI.bulkUpdateAllRegions(
        expandedServiceId,
        parseFloat(bulkPrice)
      );
      toast.success(
        `تم تحديث ${response.data.data.totalRegions} منطقة بنجاح`
      );
      
      // Refresh pricing details
      const detailsResponse = await servicesAPI.getPricingDetails(expandedServiceId);
      setPricingDetails(detailsResponse.data.data);
      setBulkPrice('');
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل التحديث الشامل');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkUpdateByGovernorate = async () => {
    if (
      !expandedServiceId ||
      !selectedGovernorate ||
      !bulkPrice ||
      parseFloat(bulkPrice) < 0
    ) {
      toast.error('يرجى اختيار المحافظة وإدخال سعر صحيح');
      return;
    }

    if (
      !window.confirm(
        `هل أنت متأكد من تحديث أسعار جميع مناطق ${selectedGovernorate} إلى ${bulkPrice} دينار؟`
      )
    ) {
      return;
    }

    setBulkUpdating(true);
    try {
      const response = await servicesAPI.bulkUpdateByGovernorate(
        expandedServiceId,
        selectedGovernorate,
        parseFloat(bulkPrice)
      );
      toast.success(
        `تم تحديث ${response.data.data.totalRegions} منطقة في ${selectedGovernorate} بنجاح`
      );
      
      // Refresh pricing details
      const detailsResponse = await servicesAPI.getPricingDetails(expandedServiceId);
      setPricingDetails(detailsResponse.data.data);
      setBulkPrice('');
      setSelectedGovernorate('');
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل التحديث الشامل');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleToggleAvailability = async (serviceId: number, regionId: number) => {
    setTogglingRegions((prev) => new Set(prev).add(regionId));
    try {
      await servicesAPI.toggleServiceAvailability(serviceId, regionId);
      toast.success('تم تحديث حالة الخدمة بنجاح');
      
      // Refresh pricing details
      const response = await servicesAPI.getPricingDetails(serviceId);
      setPricingDetails(response.data.data);
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل تحديث حالة الخدمة');
    } finally {
      setTogglingRegions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(regionId);
        return newSet;
      });
    }
  };

  const handleOpenEditPriceModal = (
    serviceId: number,
    regionId: number,
    regionName: string,
    currentPrice: number
  ) => {
    setEditPriceModal({
      show: true,
      serviceId,
      regionId,
      regionName,
      currentPrice,
      newPrice: currentPrice.toString(),
    });
  };

  const handleCloseEditPriceModal = () => {
    setEditPriceModal({
      show: false,
      serviceId: null,
      regionId: null,
      regionName: '',
      currentPrice: 0,
      newPrice: '',
    });
    setSavingModalPrice(false);
  };

  const handleSavePriceFromModal = async () => {
    if (!editPriceModal.serviceId || !editPriceModal.regionId) return;

    const priceValue = parseFloat(editPriceModal.newPrice);
    if (isNaN(priceValue) || priceValue < 0) {
      toast.error('يرجى إدخال سعر صحيح');
      return;
    }

    setSavingModalPrice(true);
    try {
      await servicesAPI.updateServiceRegionPrice(
        editPriceModal.serviceId,
        editPriceModal.regionId,
        priceValue
      );
      toast.success('تم تحديث السعر بنجاح');

      // Refresh pricing details
      const response = await servicesAPI.getPricingDetails(editPriceModal.serviceId);
      setPricingDetails(response.data.data);

      // Close modal
      handleCloseEditPriceModal();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل تحديث السعر');
      setSavingModalPrice(false);
    }
  };

  const handleRemoveServiceFromRegion = async (serviceId: number, regionId: number, regionName: string) => {
    if (
      !window.confirm(
        `هل أنت متأكد من حذف الخدمة من منطقة "${regionName}"؟`
      )
    ) {
      return;
    }

    try {
      await servicesAPI.removeServiceFromRegion(serviceId, regionId);
      toast.success('تم حذف الخدمة من المنطقة بنجاح');
      
      // Refresh pricing details
      const response = await servicesAPI.getPricingDetails(serviceId);
      setPricingDetails(response.data.data);
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل حذف الخدمة من المنطقة');
    }
  };

  const getServiceTypeLabel = (type: ServiceType) => {
    const serviceType = serviceTypes.find((t) => t.value === type);
    return serviceType ? `${serviceType.labelEn} - ${serviceType.labelAr}` : type;
  };

  // Filter services by pricing model
  const singleRegionServices = services.filter(
    (s) => !s.pricingModel || s.pricingModel === 'single_region'
  );
  const routeBasedServices = services.filter(
    (s) => s.pricingModel === 'region_to_region'
  );

  const displayedServices = serviceTypeTab === 'single_region'
    ? singleRegionServices
    : routeBasedServices;

  if (loading && services.length === 0) {
    return <Loading text="جاري تحميل الخدمات..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-primary">إدارة الخدمات</h1>
            <p className="text-sm text-gray-400">إدارة الخدمات وأسعار المناطق</p>
          </div>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="h-4 w-4 ml-2" />
          إضافة خدمة جديدة
        </Button>
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Service Type Tabs */}
      <div className="card">
        <div className="flex gap-2 border-b border-gray-700 pb-4 mb-4">
          <button
            onClick={() => setServiceTypeTab('single_region')}
            className={`px-6 py-3 font-medium rounded-lg transition-all ${
              serviceTypeTab === 'single_region'
                ? 'bg-primary text-dark shadow-glow'
                : 'text-gray-400 hover:text-gray-200 hover:bg-dark'
            }`}
          >
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              <div>
                <div className="font-bold">خدمات المناطق الفردية</div>
                <div className="text-xs opacity-75">
                  ({singleRegionServices.length} خدمة)
                </div>
              </div>
            </div>
          </button>
          <button
            onClick={() => setServiceTypeTab('route_based')}
            className={`px-6 py-3 font-medium rounded-lg transition-all ${
              serviceTypeTab === 'route_based'
                ? 'bg-primary text-dark shadow-glow'
                : 'text-gray-400 hover:text-gray-200 hover:bg-dark'
            }`}
          >
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              <div>
                <div className="font-bold">خدمات المسارات</div>
                <div className="text-xs opacity-75">
                  ({routeBasedServices.length} خدمة)
                </div>
              </div>
            </div>
          </button>
        </div>

        {serviceTypeTab === 'route_based' && routeBasedServices.length > 0 && (
          <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-400 mb-2">
              💡 <strong>ملاحظة:</strong> خدمات المسارات تستخدم نظام تسعير من منطقة إلى منطقة.
            </p>
            <p className="text-xs text-gray-400">
              لإدارة أسعار المسارات، انتقل إلى صفحة <strong>"إدارة المسارات"</strong> من القائمة الجانبية.
            </p>
          </div>
        )}
      </div>

      {/* Services Table */}
      <div className="card">
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            إجمالي الخدمات: {displayedServices.length}
          </div>
          {serviceTypeTab === 'single_region' && (
            <span className="text-xs text-gray-500">
              الخدمات بأسعار ثابتة لكل منطقة
            </span>
          )}
          {serviceTypeTab === 'route_based' && (
            <span className="text-xs text-gray-500">
              الخدمات بأسعار متغيرة حسب المسار
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>الاسم بالإنجليزية</th>
                <th>الاسم بالعربية</th>
                <th>النوع</th>
                <th>الترتيب</th>
                <th>الحالة</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {displayedServices.map((service) => (
                <Fragment key={service.id}>
                  <tr>
                    <td className="font-medium">{service.nameEn}</td>
                    <td className="font-medium">{service.nameAr}</td>
                    <td>
                      <div className="text-sm text-gray-400">
                        {getServiceTypeLabel(service.serviceType)}
                      </div>
                      {service.pricingModel && (
                        <div className="text-xs text-gray-500 mt-1">
                          {service.pricingModel === 'single_region' ? '📍 منطقة واحدة' : '🔀 مسارات'}
                        </div>
                      )}
                    </td>
                    <td className="text-gray-400">{service.displayOrder}</td>
                    <td>
                      <span
                        className={`badge ${
                          service.isActive ? 'badge-success' : 'badge-error'
                        }`}
                      >
                        {service.isActive ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleOpenModal(service)}
                          className="p-2 hover:bg-dark rounded transition-colors"
                          title="تعديل"
                        >
                          <Edit className="h-4 w-4 text-primary" />
                        </button>
                        <button
                          onClick={() => handleDelete(service)}
                          className="p-2 hover:bg-dark rounded transition-colors"
                          title="حذف"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                        {serviceTypeTab === 'single_region' && (
                          <button
                            onClick={() => handleTogglePricing(service.id)}
                            className="p-2 hover:bg-dark rounded transition-colors"
                            title="إدارة الأسعار"
                          >
                            {expandedServiceId === service.id ? (
                              <ChevronUp className="h-4 w-4 text-green-500" />
                            ) : (
                              <DollarSign className="h-4 w-4 text-green-500" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Pricing Section - Only for single region services */}
                  {serviceTypeTab === 'single_region' && expandedServiceId === service.id && (
                    <tr>
                      <td colSpan={6} className="bg-dark-lighter p-0">
                        <div className="p-6">
                          {loadingPricing ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="h-8 w-8 animate-spin text-primary" />
                              <span className="mr-3 text-gray-300">
                                جاري تحميل بيانات الأسعار...
                              </span>
                            </div>
                          ) : pricingDetails ? (
                            <div className="space-y-6">
                              {/* Header */}
                              <div className="flex items-center justify-between border-b border-gray-700 pb-4">
                                <h3 className="text-xl font-bold text-primary flex items-center gap-2">
                                  <DollarSign className="h-6 w-6" />
                                  إدارة أسعار: {service.nameAr}
                                </h3>
                                <button
                                  onClick={() => handleTogglePricing(service.id)}
                                  className="text-gray-400 hover:text-gray-200"
                                >
                                  <ChevronUp className="h-5 w-5" />
                                </button>
                              </div>

                              {/* Statistics */}
                              {pricingDetails.statistics && (
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                  <div className="bg-dark rounded-lg p-4">
                                    <div className="text-sm text-gray-400">
                                      إجمالي المناطق
                                    </div>
                                    <div className="text-2xl font-bold text-primary">
                                      {pricingDetails.totalRegionsWithPricing}
                                    </div>
                                  </div>
                                  <div className="bg-dark rounded-lg p-4">
                                    <div className="text-sm text-gray-400">أقل سعر</div>
                                    <div className="text-2xl font-bold text-green-500">
                                      {pricingDetails.statistics.min.toFixed(3)} د.ك
                                    </div>
                                  </div>
                                  <div className="bg-dark rounded-lg p-4">
                                    <div className="text-sm text-gray-400">أعلى سعر</div>
                                    <div className="text-2xl font-bold text-yellow-500">
                                      {pricingDetails.statistics.max.toFixed(3)} د.ك
                                    </div>
                                  </div>
                                  <div className="bg-dark rounded-lg p-4">
                                    <div className="text-sm text-gray-400">
                                      متوسط السعر
                                    </div>
                                    <div className="text-2xl font-bold text-blue-500">
                                      {pricingDetails.statistics.average.toFixed(3)} د.ك
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Tabs */}
                              <div className="flex gap-2 border-b border-gray-700">
                                <button
                                  onClick={() => setActiveTab('individual')}
                                  className={`px-4 py-2 font-medium transition-colors ${
                                    activeTab === 'individual'
                                      ? 'text-primary border-b-2 border-primary'
                                      : 'text-gray-400 hover:text-gray-200'
                                  }`}
                                >
                                  الأسعار الفردية
                                </button>
                                <button
                                  onClick={() => setActiveTab('bulk')}
                                  className={`px-4 py-2 font-medium transition-colors ${
                                    activeTab === 'bulk'
                                      ? 'text-primary border-b-2 border-primary'
                                      : 'text-gray-400 hover:text-gray-200'
                                  }`}
                                >
                                  التحديث الشامل
                                </button>
                              </div>

                              {/* Tab Content */}
                              {activeTab === 'individual' ? (
                                <div className="space-y-6">
                                  {pricingDetails.governorates.map((gov) => (
                                    <div
                                      key={gov.governorateAr}
                                      className="bg-dark rounded-lg p-4"
                                    >
                                      <h4 className="text-lg font-bold text-primary mb-3">
                                        {gov.governorateAr} ({gov.governorateEn})
                                      </h4>
                                      <div className="overflow-x-auto">
                                        <table className="w-full">
                                          <thead>
                                            <tr className="text-right text-sm text-gray-400 border-b border-gray-700">
                                              <th className="pb-2">المنطقة</th>
                                              <th className="pb-2">السعر (د.ك)</th>
                                              <th className="pb-2">الحالة</th>
                                              <th className="pb-2">الإجراءات</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {gov.regions.map((region) => {
                                              const isEditing =
                                                region.regionId in editingPrices;
                                              const isSaving =
                                                savingRegions.has(region.regionId);
                                              const isToggling =
                                                togglingRegions.has(region.regionId);
                                              const currentPrice = isEditing
                                                ? editingPrices[region.regionId]
                                                : region.price.toString();

                                              return (
                                                <tr
                                                  key={region.regionId}
                                                  className={`border-b border-gray-800 hover:bg-dark-lighter/50 ${
                                                    !region.isAvailable ? 'opacity-60' : ''
                                                  }`}
                                                >
                                                  <td className="py-3">
                                                    {region.regionNameAr}
                                                    <span className="text-sm text-gray-400 mr-2">
                                                      ({region.regionNameEn})
                                                    </span>
                                                  </td>
                                                  <td className="py-3">
                                                    {isEditing ? (
                                                      <Input
                                                        type="number"
                                                        step="0.001"
                                                        min="0"
                                                        value={currentPrice}
                                                        onChange={(e) =>
                                                          handlePriceChange(
                                                            region.regionId,
                                                            e.target.value
                                                          )
                                                        }
                                                        className="max-w-[150px]"
                                                        disabled={isSaving}
                                                      />
                                                    ) : (
                                                      <span
                                                        onClick={() =>
                                                          handlePriceChange(
                                                            region.regionId,
                                                            region.price.toString()
                                                          )
                                                        }
                                                        className="cursor-pointer hover:text-primary transition-colors"
                                                      >
                                                        {region.price.toFixed(3)}
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td className="py-3">
                                                    <span
                                                      className={`badge ${
                                                        region.isAvailable
                                                          ? 'badge-success'
                                                          : 'badge-error'
                                                      }`}
                                                    >
                                                      {region.isAvailable
                                                        ? 'نشط'
                                                        : 'غير نشط'}
                                                    </span>
                                                  </td>
                                                  <td className="py-3">
                                                    <div className="flex gap-2">
                                                      <button
                                                        onClick={() =>
                                                          handleOpenEditPriceModal(
                                                            service.id,
                                                            region.regionId,
                                                            region.regionNameAr,
                                                            region.price
                                                          )
                                                        }
                                                        className="p-1.5 hover:bg-dark rounded transition-colors text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                        title="تحديث السعر"
                                                      >
                                                        <PencilLine className="h-4 w-4" />
                                                      </button>
                                                      <button
                                                        onClick={() =>
                                                          handleToggleAvailability(
                                                            service.id,
                                                            region.regionId
                                                          )
                                                        }
                                                        disabled={isToggling}
                                                        className="p-1.5 hover:bg-dark rounded transition-colors disabled:opacity-50"
                                                        title={
                                                          region.isAvailable
                                                            ? 'تعطيل الخدمة'
                                                            : 'تفعيل الخدمة'
                                                        }
                                                      >
                                                        {isToggling ? (
                                                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                                        ) : region.isAvailable ? (
                                                          <ToggleRight className="h-4 w-4 text-green-500" />
                                                        ) : (
                                                          <ToggleLeft className="h-4 w-4 text-gray-500" />
                                                        )}
                                                      </button>
                                                      <button
                                                        onClick={() =>
                                                          handleRemoveServiceFromRegion(
                                                            service.id,
                                                            region.regionId,
                                                            region.regionNameAr
                                                          )
                                                        }
                                                        className="p-1.5 hover:bg-dark rounded transition-colors text-red-500 hover:text-red-400"
                                                        title="حذف من المنطقة"
                                                      >
                                                        <X className="h-4 w-4" />
                                                      </button>
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="space-y-6">
                                  {/* Update All Regions */}
                                  <div className="bg-dark rounded-lg p-6">
                                    <h4 className="text-lg font-bold text-primary mb-4">
                                      تحديث جميع المناطق
                                    </h4>
                                    <p className="text-sm text-gray-400 mb-4">
                                      سيتم تطبيق السعر الجديد على جميع المناطق في
                                      الكويت
                                    </p>
                                    <div className="flex gap-3 items-end">
                                      <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                          السعر الجديد (د.ك)
                                        </label>
                                        <Input
                                          type="number"
                                          step="0.001"
                                          min="0"
                                          value={bulkPrice}
                                          onChange={(e) =>
                                            setBulkPrice(e.target.value)
                                          }
                                          placeholder="0.000"
                                          disabled={bulkUpdating}
                                        />
                                      </div>
                                      <Button
                                        onClick={handleBulkUpdateAll}
                                        disabled={
                                          !bulkPrice || bulkUpdating
                                        }
                                        className="flex items-center gap-2"
                                      >
                                        {bulkUpdating ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <RefreshCw className="h-4 w-4" />
                                        )}
                                        تحديث الكل
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Update by Governorate */}
                                  <div className="bg-dark rounded-lg p-6">
                                    <h4 className="text-lg font-bold text-primary mb-4">
                                      تحديث حسب المحافظة
                                    </h4>
                                    <p className="text-sm text-gray-400 mb-4">
                                      اختر محافظة لتحديث أسعار جميع مناطقها
                                    </p>
                                    <div className="flex gap-3 items-end">
                                      <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                          المحافظة
                                        </label>
                                        <select
                                          value={selectedGovernorate}
                                          onChange={(e) =>
                                            setSelectedGovernorate(e.target.value)
                                          }
                                          disabled={bulkUpdating}
                                          className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2.5 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                                        >
                                          <option value="">اختر المحافظة</option>
                                          {pricingDetails.allGovernorates.map(
                                            (gov) => (
                                              <option
                                                key={`${gov.ar}-${gov.en}`}
                                                value={gov.ar}
                                              >
                                                {gov.ar} ({gov.en})
                                              </option>
                                            )
                                          )}
                                        </select>
                                      </div>
                                      <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                          السعر الجديد (د.ك)
                                        </label>
                                        <Input
                                          type="number"
                                          step="0.001"
                                          min="0"
                                          value={bulkPrice}
                                          onChange={(e) =>
                                            setBulkPrice(e.target.value)
                                          }
                                          placeholder="0.000"
                                          disabled={bulkUpdating}
                                        />
                                      </div>
                                      <Button
                                        onClick={handleBulkUpdateByGovernorate}
                                        disabled={
                                          !selectedGovernorate ||
                                          !bulkPrice ||
                                          bulkUpdating
                                        }
                                        className="flex items-center gap-2"
                                      >
                                        {bulkUpdating ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <RefreshCw className="h-4 w-4" />
                                        )}
                                        تحديث المحافظة
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-400">
                              لا توجد بيانات أسعار
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Service Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-dark-lighter border border-gray-700 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-primary mb-4">
              {editingService ? 'تعديل خدمة' : 'إضافة خدمة جديدة'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  الاسم بالإنجليزية *
                </label>
                <Input
                  type="text"
                  value={formData.nameEn}
                  onChange={(e) =>
                    setFormData({ ...formData, nameEn: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  الاسم بالعربية *
                </label>
                <Input
                  type="text"
                  value={formData.nameAr}
                  onChange={(e) =>
                    setFormData({ ...formData, nameAr: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  الوصف بالإنجليزية
                </label>
                <textarea
                  value={formData.descriptionEn}
                  onChange={(e) =>
                    setFormData({ ...formData, descriptionEn: e.target.value })
                  }
                  rows={3}
                  className="w-full bg-dark border border-gray-700 rounded-lg px-4 py-2.5 text-gray-200 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  الوصف بالعربية
                </label>
                <textarea
                  value={formData.descriptionAr}
                  onChange={(e) =>
                    setFormData({ ...formData, descriptionAr: e.target.value })
                  }
                  rows={3}
                  className="w-full bg-dark border border-gray-700 rounded-lg px-4 py-2.5 text-gray-200 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  نوع الخدمة *
                </label>
                <select
                  value={formData.serviceType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      serviceType: e.target.value as ServiceType,
                    })
                  }
                  required
                  className="w-full bg-dark border border-gray-700 rounded-lg px-4 py-2.5 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                >
                  {serviceTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.labelEn} - {type.labelAr}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  الأيقونة (اختياري)
                </label>
                <Input
                  type="text"
                  value={formData.icon || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, icon: e.target.value })
                  }
                  placeholder="مثال: tire, battery, fuel"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  ترتيب العرض
                </label>
                <Input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="rounded border-gray-700"
                />
                <label htmlFor="isActive" className="text-sm text-gray-300">
                  خدمة نشطة
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={submitting} className="flex-1">
                  {submitting
                    ? 'جاري الحفظ...'
                    : editingService
                    ? 'تحديث'
                    : 'إضافة'}
                </Button>
                <Button
                  type="button"
                  onClick={handleCloseModal}
                  variant="secondary"
                  disabled={submitting}
                  className="flex-1"
                >
                  إلغاء
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Price Modal */}
      {editPriceModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-dark-lighter border border-gray-700 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-primary flex items-center gap-2">
                <PencilLine className="h-5 w-5" />
                تحديث السعر
              </h2>
              <button
                onClick={handleCloseEditPriceModal}
                className="text-gray-400 hover:text-gray-200"
                disabled={savingModalPrice}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Region Info */}
              <div className="bg-dark rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-1">المنطقة</div>
                <div className="text-lg font-medium text-gray-200">
                  {editPriceModal.regionName}
                </div>
              </div>

              {/* Current Price */}
              <div className="bg-dark rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-1">السعر الحالي</div>
                <div className="text-lg font-bold text-primary">
                  {editPriceModal.currentPrice.toFixed(3)} د.ك
                </div>
              </div>

              {/* New Price Input */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  السعر الجديد (د.ك) *
                </label>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  value={editPriceModal.newPrice}
                  onChange={(e) =>
                    setEditPriceModal({
                      ...editPriceModal,
                      newPrice: e.target.value,
                    })
                  }
                  placeholder="0.000"
                  disabled={savingModalPrice}
                  autoFocus
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSavePriceFromModal}
                  disabled={
                    savingModalPrice ||
                    !editPriceModal.newPrice ||
                    parseFloat(editPriceModal.newPrice) < 0
                  }
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  {savingModalPrice ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      جاري الحفظ...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      حفظ السعر
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={handleCloseEditPriceModal}
                  variant="secondary"
                  disabled={savingModalPrice}
                  className="flex-1"
                >
                  إلغاء
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceManagementPage;
